import React, { useState } from "react";

import { changeInsurance } from "../../slices/actions";
import { toggleModal } from "../../slices/popupSlice";
import {
	insuranceAdded,
	insuranceRemoved,
	prepareInsuranceItem,
	trackPopupViewEvent,
} from "../../tracking/googleAnalytics";
import {
	insuranceDataObject,
	pushEventToOptimizely,
} from "../../tracking/optimizely";
import { useDispatch, useSelector } from "../../utils/hooks";
import strings from "../../utils/strings";
import {
	INSURANCE_MODE,
	INSURANCE_TYPE,
	INSURANCE_VARIANT,
} from "../products/Insurance.type";
import "./Modal.scss";
import "../products/Insurance.scss";

export const ScreenInsuranceModal = () => {
	const dispatch = useDispatch();

	const cartId = useSelector(
		(state) => state.popup.products.mainProduct.cartId,
	);

	const cartItemId = useSelector(
		(state) => state.popup.products.mainProduct.cartItemId,
	);

	const insurance = useSelector(
		(state) => state.popup.products.mainProduct.insurance,
	);

	const selectedInsuranceId = useSelector(
		(state) => state.popup.products.mainProduct.selectedInsuranceId,
	);

	const redirectUrl = useSelector(
		(state) => state.popup.modal?.config?.redirectUrl,
	);

	const productId = useSelector(
		(state) => state.popup.products.mainProduct.insuranceProductId,
	);

	const productName = useSelector(
		(state) => state.popup.products.mainProduct.productName,
	);

	const isFlexInsurance = useSelector(
		(state) => state.popup.products.mainProduct.isFlexInsurance,
	);

	const standardInsuraces = insurance.filter((insuranceLine) => {
		return insuranceLine.variant === INSURANCE_VARIANT.REGULAR;
	});

	const screenInsuraces = insurance.filter((insuranceLine) => {
		return insuranceLine.variant === INSURANCE_VARIANT.SCREEN;
	});

	const [currentInsuranceId, setCurrentInsuranceId] = useState(
		selectedInsuranceId || standardInsuraces[0].id,
	);

	const chooseInsurance = ({ event, insurance, mode }) => {
		let id = null;
		if (mode === INSURANCE_MODE.REMOVE) {
			event.preventDefault();
			const element = document.getElementById(
				currentInsuranceId,
			) as HTMLInputElement;

			if (element) {
				element.checked = false;
			}
		} else {
			id = insurance.id;
		}

		setCurrentInsuranceId(id);

		return dispatch(
			changeInsurance({
				cartId,
				cartItemId,
				insuranceId: id,
			}),
		)
			.unwrap()
			.then((response) => response);
	};

	const handleChange = async (event, mode) => {
		event.preventDefault();
		const insuranceLine = insurance.find(
			(insuranceLine) => insuranceLine.id === currentInsuranceId,
		);

		if (insuranceLine) {
			const response = await chooseInsurance({
				event,
				insurance: insuranceLine,
				mode,
			});

			const insuranceItem = prepareInsuranceItem({
				id: insuranceLine.id,
				name: insuranceLine.description,
				price: insuranceLine.price,
				variant: isFlexInsurance ? INSURANCE_TYPE.FLEX : INSURANCE_TYPE.REGULAR,
			});

			if (mode === INSURANCE_MODE.CHANGE) {
				insuranceAdded(insuranceItem);
			} else if (selectedInsuranceId) {
				insuranceRemoved(insuranceItem);
			}

			trackPopupViewEvent({
				category: "Information",
				name:
					mode === INSURANCE_MODE.CHANGE
						? "Insurance modal closed (insurance set)"
						: "Insurance modal closed (insurance removed)",
			});

			if (response && redirectUrl) {
				window.location.href = redirectUrl;
			}
		}

		dispatch(toggleModal({ id: "", isOpen: false, config: null }));
	};

	if (!insurance || insurance.length === 0) {
		return null;
	}

	return (
		<div className="addtocartpopupModal--container addtocartpopupModal--container-column">
			<div className="addtocartpopupModal--iconcontainer">
				<img data-testid="product-image" src={`/img/p/100/${productId}.jpg`} />
				<p>{productName}</p>
			</div>
			<div className="addtocartpopupModal--row">
				<section className="addtocart-insurance__container">
					<form
						className="addtocart-insurance addtocart-insurance--screen-insurance"
						aria-labelledby="insurance__heading"
					>
						<div className="addtocart-insurance__info">
							<h2>{strings["addtocartpopup.insurance.subtitle"]}</h2>
						</div>

						<div className="addtocart-insurance__columns">
							<div className="addtocart-insurance__inputs--box">
								<div className="addtocartpopupModal--wrapper">
									<div className="addtocartpopupModal--content addtocartpopupModal--content__default">
										<h4>
											{isFlexInsurance
												? strings["addtocartpopup.flex.insurance.title"]
												: strings["addtocartpopup.insurance.title"]}
										</h4>

										<div
											className="addtocartpopupModal--row"
											dangerouslySetInnerHTML={{
												__html: strings["addtocartpopup.mobile.insurance.list"],
											}}
										/>
									</div>
								</div>
								<div className="addtocart-insurance__inputs">
									{standardInsuraces?.map((insuranceLine) => (
										<div
											className="addtocart-insurance__input-wrapper"
											key={insuranceLine.id}
										>
											<input
												type={isFlexInsurance ? "checkbox" : "radio"}
												id={insuranceLine.id}
												checked={insuranceLine.id === currentInsuranceId}
												data-testid={`insurance-option-input-${insuranceLine.id}`}
												name="insurance"
												value={insuranceLine.description}
												onChange={(event) => {
													chooseInsurance({
														event,
														insurance: insuranceLine,
														mode:
															insuranceLine.id === currentInsuranceId
																? INSURANCE_MODE.REMOVE
																: INSURANCE_MODE.CHANGE,
													});
												}}
											/>
											<label
												htmlFor={insuranceLine.id}
												data-testid={`insurance-option-label-${insuranceLine.id}`}
												className="addtocart-input__label"
											>
												<span className="addtocart-input__label--description">
													{isFlexInsurance
														? insuranceLine.subscriptionDescription
														: insuranceLine.description}
												</span>

												<span className="addtocart-input__label--price">
													{isFlexInsurance ? (
														<>
															{insuranceLine.monthlyPriceNet}
															{strings["subscription.price.permonth"]}
														</>
													) : (
														insuranceLine.priceNet
													)}
												</span>
											</label>
										</div>
									))}
								</div>
							</div>

							<div className="addtocart-insurance__inputs--box">
								<div className="addtocartpopupModal--wrapper">
									<div className="addtocartpopupModal--content addtocartpopupModal--content__default">
										<h4>{strings["addtocartpopup.screen.insurance.title"]}</h4>
										<div
											className="addtocartpopupModal--row"
											dangerouslySetInnerHTML={{
												__html: strings["addtocartpopup.screen.insurance.list"],
											}}
										/>
									</div>
								</div>
								<div className="addtocart-insurance__inputs">
									{screenInsuraces?.map((insuranceLine) => (
										<div
											className="addtocart-insurance__input-wrapper"
											key={insuranceLine.id}
										>
											<input
												type={isFlexInsurance ? "checkbox" : "radio"}
												id={insuranceLine.id}
												checked={insuranceLine.id === currentInsuranceId}
												data-testid={`insurance-option-input-${insuranceLine.id}`}
												name="insurance"
												value={insuranceLine.description}
												onChange={(event) => {
													chooseInsurance({
														event,
														insurance: insuranceLine,
														mode:
															insuranceLine.id === currentInsuranceId
																? INSURANCE_MODE.REMOVE
																: INSURANCE_MODE.CHANGE,
													});
												}}
											/>
											<label
												htmlFor={insuranceLine.id}
												data-testid={`insurance-option-label-${insuranceLine.id}`}
												className="addtocart-input__label"
											>
												<span className="addtocart-input__label--description">
													{isFlexInsurance
														? insuranceLine.subscriptionDescription
														: insuranceLine.description}
												</span>
												<span className="addtocart-input__label--price">
													{isFlexInsurance ? (
														<>
															{insuranceLine.monthlyPriceNet}
															{strings["subscription.price.permonth"]}
														</>
													) : (
														insuranceLine.priceNet
													)}
												</span>
											</label>
										</div>
									))}
								</div>
							</div>
						</div>

						<div className="addtocart-insurance__buttons caas">
							<button
								className="addtocart-button btn btn-medium tertiary-grey"
								data-testid="footer-button-continue"
								onClick={(event) => handleChange(event, INSURANCE_MODE.REMOVE)}
							>
								<span>
									{strings["addtocartpopup.insurance.modal.button.no"]}
								</span>
							</button>
							<button
								className="addtocart-button btn btn-medium primary"
								data-testid="footer-button-gotocart"
								onClick={(event) => handleChange(event, INSURANCE_MODE.CHANGE)}
							>
								<span>
									{strings["addtocartpopup.insurance.modal.button.yes"]}
								</span>
							</button>
						</div>
					</form>
				</section>
			</div>
		</div>
	);
};
